"use client";

import { apiFetch } from "@/src/lib/api-fetch";
import { Fragment, useState, useCallback, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { RefreshCw } from "lucide-react";

export interface RegenerateLessonDialogProps {
    isOpen: boolean;
    onClose: () => void;
    /** Current generated_text (canonical markdown), used as fallback when no chunks exist */
    generatedText: string;
    /** Lesson review item UUID (e.g. 46ee23ba-f0f4-4b8c-b62e-27338f03bcc6) */
    itemUuid: string;
    /** Canonical numeric lesson id used as current_lesson_id in tasks */
    canonicalLessonId?: number | null;
    /** Current lesson heading/title */
    lessonTitle?: string;
    /** Current key concepts array from generated_lessons.key_concepts */
    initialKeyConcepts?: string[];
    /** Called after successful save + dispatch (e.g. refresh parent data) */
    onSuccess: () => void;
    /** Called when canonical regeneration starts, used for UI + polling */
    onRegenerationStarted?: () => void;
    /** Optional toast callback */
    onToast?: (message: string, type: "success" | "error") => void;
}

export default function RegenerateLessonDialog({
    isOpen,
    onClose,
    generatedText,
    itemUuid,
    canonicalLessonId,
    lessonTitle = "",
    initialKeyConcepts = [],
    onSuccess,
    onToast,
    onRegenerationStarted,
}: RegenerateLessonDialogProps) {
    const [title, setTitle] = useState(lessonTitle);
    const [keyConceptsText, setKeyConceptsText] = useState(
        initialKeyConcepts.join("\n"),
    );
    const [chunks, setChunks] = useState<{ id?: number; text: string }[]>([]);
    const [isLoadingChunks, setIsLoadingChunks] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Load chunks for the canonical lesson when dialog opens
    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;

        const load = async () => {
            setIsLoadingChunks(true);
            setError(null);
            try {
                const res = await apiFetch(`/api/admin/review/lesson/${itemUuid}/regenerate`);
                if (!res.ok) {
                    const data = await res.json().catch(() => ({}));
                    if (!cancelled) {
                        setError(data.error ?? "Failed to load lesson chunks");
                    }
                    return;
                }
                const data = await res.json();
                const remoteChunks = Array.isArray(data.chunks)
                    ? (data.chunks as Array<{ id?: number; chunk_text?: string }>).map((c) => ({
                        id: c.id as number | undefined,
                        text: String(c.chunk_text ?? ""),
                    }))
                    : [];

                if (!cancelled) {
                    setChunks(remoteChunks);
                    const remoteTitle =
                        typeof data.lesson_title === "string" && data.lesson_title.trim().length > 0
                            ? data.lesson_title
                            : lessonTitle;
                    const remoteKeyConcepts = Array.isArray(data.key_concepts)
                        ? (data.key_concepts as unknown[]).map((k) => String(k))
                        : initialKeyConcepts;

                    setTitle(remoteTitle);
                    setKeyConceptsText(remoteKeyConcepts.join("\n"));
                }
            } catch (e) {
                if (!cancelled) {
                    setError("Failed to load lesson chunks");
                }
            } finally {
                if (!cancelled) setIsLoadingChunks(false);
            }
        };

        void load();

        return () => {
            cancelled = true;
        };
    }, [isOpen, itemUuid, generatedText, lessonTitle, initialKeyConcepts]);

    const resetForm = useCallback(() => {
        setTitle(lessonTitle);
        setKeyConceptsText(initialKeyConcepts.join("\n"));
        setError(null);
    }, [lessonTitle, initialKeyConcepts]);

    const handleClose = useCallback(() => {
        if (!isSubmitting) {
            resetForm();
            onClose();
        }
    }, [isSubmitting, resetForm, onClose]);

    const handleRegenerate = async () => {
        setError(null);
        setIsSubmitting(true);
        try {
            const body: Record<string, unknown> = {
                change_note: "Regenerate with edits",
            };

            const normalizedChunks = chunks
                .filter((c) => typeof c.id === "number")
                .map((c) => ({
                    id: c.id as number,
                    text: (c.text ?? "").trim(),
                }))
                .filter((c) => c.text.length > 0);

            if (!normalizedChunks.length) {
                setError("Please provide at least one non-empty existing chunk.");
                setIsSubmitting(false);
                return;
            }

            body.chunks = normalizedChunks;

            const trimmedTitle = title.trim();
            if (trimmedTitle.length > 0) {
                body.title = trimmedTitle;
            }

            const keyConcepts = keyConceptsText
                .split("\n")
                .map((s) => s.trim())
                .filter((s) => s.length > 0);
            if (keyConcepts.length > 0) {
                body.key_concepts = keyConcepts;
            }

            const res = await apiFetch(`/api/admin/review/lesson/${itemUuid}/regenerate`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });

            const data = await res.json().catch(() => ({}));

            if (!res.ok) {
                setError(data.error ?? "Failed to regenerate lesson");
                return;
            }

            onToast?.("Content saved and regeneration task dispatched.", "success");
            onRegenerationStarted?.();
            onSuccess();
            handleClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Request failed");
            onToast?.("Failed to regenerate lesson.", "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Transition.Root show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={handleClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-slate-500/75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="relative w-full max-w-3xl transform overflow-hidden rounded-xl bg-white shadow-xl transition-all">
                                <div className="px-6 pt-6 pb-4">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-indigo-100">
                                            <RefreshCw className="h-5 w-5 text-indigo-600" aria-hidden="true" />
                                        </div>
                                        <div>
                                            <Dialog.Title as="h3" className="text-lg font-semibold text-slate-900">
                                                Regenerate lesson
                                            </Dialog.Title>
                                            <p className="text-sm text-slate-500 mt-0.5">
                                                This will use the canonical lesson id as <span className="font-mono text-slate-700">current_lesson_id</span> for the task.
                                            </p>
                                            {canonicalLessonId != null && (
                                                <p className="mt-1 text-xs text-slate-500">
                                                    <span className="font-semibold">current_lesson_id:</span>{" "}
                                                    <span className="font-mono">{canonicalLessonId}</span>
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="space-y-4 mt-4">
                                        <div className="grid gap-3 md:grid-cols-2">
                                            <div>
                                                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                                                    Lesson heading
                                                </label>
                                                <input
                                                    type="text"
                                                    className="w-full rounded-lg border border-slate-200 bg-slate-50/50 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                                                    placeholder="Lesson title..."
                                                    value={title}
                                                    onChange={(e) => setTitle(e.target.value)}
                                                    disabled={isSubmitting}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                                                    Key concepts (one per line)
                                                </label>
                                                <textarea
                                                    rows={4}
                                                    className="w-full rounded-lg border border-slate-200 bg-slate-50/50 px-3 py-2 text-xs text-slate-900 placeholder:text-slate-400 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 font-mono"
                                                    placeholder={"Concept 1\nConcept 2\nConcept 3"}
                                                    value={keyConceptsText}
                                                    onChange={(e) => setKeyConceptsText(e.target.value)}
                                                    disabled={isSubmitting}
                                                />
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider">
                                                Content chunks for current_lesson_id
                                            </label>
                                            {isLoadingChunks && (
                                                <p className="text-xs text-slate-500">Loading chunks…</p>
                                            )}
                                            {!isLoadingChunks && chunks.map((chunk, index) => (
                                                <div key={chunk.id ?? index} className="space-y-1">
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-[11px] font-semibold text-slate-500">
                                                            Chunk {index + 1}
                                                        </span>
                                                    </div>
                                                    <textarea
                                                        rows={3}
                                                        className="w-full rounded-lg border border-slate-200 bg-slate-50/50 px-3 py-1.5 text-xs text-slate-900 font-mono"
                                                        value={chunk.text}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            setChunks((prev) =>
                                                                prev.map((c, i) =>
                                                                    i === index ? { ...c, text: value } : c,
                                                                ),
                                                            );
                                                        }}
                                                        disabled={isSubmitting}
                                                    />
                                                </div>
                                            ))}
                                        </div>

                                        {error && (
                                            <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg border border-red-100">
                                                {error}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <div className="flex flex-row-reverse gap-3 px-6 py-4 bg-slate-50 border-t border-slate-200">
                                    <button
                                        type="button"
                                        onClick={handleRegenerate}
                                        disabled={isSubmitting}
                                        className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                                Saving & sending…
                                            </>
                                        ) : (
                                            <>
                                                <RefreshCw className="h-4 w-4" />
                                                Regenerate
                                            </>
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleClose}
                                        disabled={isSubmitting}
                                        className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}
